import { useMemo } from 'react';

import * as THREE from 'three';

import { colors } from '@ui';

import type { FrameFirstClubTrack } from '../skeletonUtils';

const SHAFT_RADIUS = 0.005;
const MARKER_RADIUS = 0.01;
const TOTAL_SEGMENTS = 32;

type GolfClubModelProps = {
    gripPoint: THREE.Vector3;
    headPoint: THREE.Vector3;
    frozen?: boolean;
    isComparison?: boolean;
};

export const GolfClubModel = ({ gripPoint, headPoint, frozen = false, isComparison = false }: GolfClubModelProps) => {
    const clubDirectionVector = headPoint.clone().sub(gripPoint);
    const clubLength = clubDirectionVector.length();
    const clubCenterPoint = gripPoint.clone().add(headPoint).multiplyScalar(0.5);

    const golfClubRotationQuaternion = new THREE.Quaternion();
    const normalizedClubDirectionVector = clubDirectionVector.clone().normalize();
    golfClubRotationQuaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normalizedClubDirectionVector);

    const gripLength = clubLength * 0.2;
    const shaftLength = clubLength * 0.8;

    const gripOffset = -(clubLength / 2) + gripLength / 2;
    const shaftOffset = gripOffset + gripLength / 2 + shaftLength / 2;

    const opacity = isComparison ? 0.5 : frozen ? 0.3 : 1;
    const markerColor = isComparison ? colors.slate[400] : colors.slate[500];
    const gripColor = isComparison ? colors.slate[400] : colors.bluegray[500];
    const shaftColor = isComparison ? colors.slate[300] : colors.bluegray[300];

    return (
        <group>
            <mesh position={gripPoint}>
                <sphereGeometry args={[MARKER_RADIUS, 16, 16]} />
                <meshBasicMaterial color={markerColor} transparent opacity={opacity} />
            </mesh>
            <mesh position={headPoint}>
                <sphereGeometry args={[MARKER_RADIUS, 16, 16]} />
                <meshBasicMaterial color={markerColor} transparent opacity={opacity} />
            </mesh>

            <group position={clubCenterPoint} quaternion={golfClubRotationQuaternion}>
                <mesh position-y={gripOffset}>
                    <cylinderGeometry args={[SHAFT_RADIUS * 1.3, SHAFT_RADIUS * 2, gripLength, TOTAL_SEGMENTS]} />
                    <meshBasicMaterial color={gripColor} transparent opacity={opacity} />
                </mesh>

                <mesh position-y={shaftOffset}>
                    <cylinderGeometry args={[SHAFT_RADIUS, SHAFT_RADIUS, shaftLength, TOTAL_SEGMENTS]} />
                    <meshBasicMaterial color={shaftColor} transparent opacity={opacity} />
                </mesh>
            </group>
        </group>
    );
};

type ClubProps = {
    frame?: number;
    clubTrack: FrameFirstClubTrack;
    comparisonFrame: number | null;
    isComparison?: boolean;
};

export function Club({ frame = 0, comparisonFrame, clubTrack, isComparison = false }: ClubProps) {
    const shaftStartFrames = clubTrack['shaft_start_coordinates'];
    const shaftEndFrames = clubTrack['shaft_end_coordinates'];

    const start = shaftStartFrames[frame] ?? shaftStartFrames[shaftStartFrames.length - 1];
    const end = shaftEndFrames[frame] ?? shaftEndFrames[shaftEndFrames.length - 1];

    const renderComparison = useMemo(() => {
        if (typeof comparisonFrame !== 'number') {
            return null;
        }

        const start = shaftStartFrames[comparisonFrame] ?? shaftStartFrames[shaftStartFrames.length - 1];
        const end = shaftEndFrames[comparisonFrame] ?? shaftEndFrames[shaftEndFrames.length - 1];

        return <GolfClubModel frozen gripPoint={new THREE.Vector3(...start)} headPoint={new THREE.Vector3(...end)} />;
    }, [comparisonFrame, shaftStartFrames, shaftEndFrames]);

    return (
        <group>
            <GolfClubModel
                gripPoint={new THREE.Vector3(...start)}
                headPoint={new THREE.Vector3(...end)}
                isComparison={isComparison}
            />
            {renderComparison}
        </group>
    );
}
